import { Training } from '../models/training.models';
import { BaseService } from './base.service';
import { User } from '../models/user.models';
import { GetSchedulePayload, GetAthleteSchedulePayload, SearchTrainingPayload } from '../models/service.models';

export default class TrainingsService extends BaseService {
  public async getTrainings2(payload: GetSchedulePayload) {
    const user: User = JSON.parse(window.localStorage.getItem('user') as string);
    const params = new URLSearchParams({ ...payload }).toString();
    return this.axios.get(`${this.backendUrl}/v2/trainings?${params}`, {
      headers: {
        'Measurement-System': user.measurementSystem,
      },
    });
  }

  public async getTraining2(id: number, type?: 'athlete' | 'coach' | 'guardian') {
    const user: User = JSON.parse(window.localStorage.getItem('user') as string);
    const params = new URLSearchParams({ type }).toString();
    return this.axios.get(`${this.backendUrl}/v2/training/${id}?${params}`, {
      headers: {
        'Measurement-System': user.measurementSystem,
      },
    });
  }

  public async getTrainingSummaryStats(payload: GetSchedulePayload) {
    const user: User = JSON.parse(window.localStorage.getItem('user') as string);
    const params = new URLSearchParams({ ...payload }).toString();
    return this.axios.get(`${this.backendUrl}/v2/trainings/summary?${params}`, {
      headers: {
        'Measurement-System': user.measurementSystem,
      },
    });
  }

  /**
   * Coach fetches athlete schedule.
   * GET /v2/team/<team_id>/athlete/<member_id>/trainings
   */
  public async getAthleteSchedule(teamId: string, memberId: string, payload: GetAthleteSchedulePayload) {
    const user: User = JSON.parse(window.localStorage.getItem('user') as string);
    const params = new URLSearchParams();
    params.set('startTime', payload.startTime);
    params.set('endTime', payload.endTime);
    if (payload.team != null && payload.team !== '') params.set('team', payload.team);
    if (payload.group != null && payload.group !== '') params.set('group', payload.group);
    return this.axios.get(`${this.backendUrl}/v2/team/${teamId}/athlete/${memberId}/trainings?${params.toString()}`, {
      headers: {
        'Measurement-System': user.measurementSystem ?? 'imperial',
      },
    });
  }

  /**
   * Coach fetches a single athlete's training details.
   * GET /v2/team/<team_id>/athlete/<member_id>/training/<id>
   */
  public async getAthleteTrainingForCoach(teamId: string, memberId: string | number, trainingId: string | number) {
    const user: User = JSON.parse(window.localStorage.getItem('user') as string);
    return this.axios.get(`${this.backendUrl}/v2/team/${teamId}/athlete/${memberId}/training/${trainingId}`, {
      headers: {
        'Measurement-System': user.measurementSystem ?? 'imperial',
      },
    });
  }

  /////////////////////////////////////////////////////////////////
  /***************        Trainings         **********************/
  /////////////////////////////////////////////////////////////////

  public async createTraining(payload: any) {
    const user: User = JSON.parse(window.localStorage.getItem('user') as string);
    return this.axios.post(`${this.backendUrl}/coach/trainings`, payload, {
      headers: {
        'Measurement-System': user.measurementSystem,
      },
    });
  }

  public async createTrainingDraftByTeamId(teamId: string, payload: any) {
    const user: User = JSON.parse(window.localStorage.getItem('user') as string);
    return this.axios.post(`${this.backendUrl}/team/${teamId}/trainings`, payload, {
      headers: {
        'Measurement-System': user.measurementSystem,
      },
    });
  }

  public async getTrainings(payload: SearchTrainingPayload) {
    const user: User = JSON.parse(window.localStorage.getItem('user') as string);
    const params = new URLSearchParams({
      start: payload.start,
      end: payload.end,
    }).toString();
    return this.axios.get(`${this.backendUrl}/coach/trainings?${params}`, {
      headers: {
        'Measurement-System': user.measurementSystem,
      },
    });
  }

  public async getTraining(id: string) {
    const user: User = JSON.parse(window.localStorage.getItem('user') as string);
    return this.axios.get(`${this.backendUrl}/coach/training/${id}`, {
      headers: {
        'Measurement-System': user.measurementSystem,
      },
    });
  }

  public async getTrainingsByTeamId(teamId: string, params: string) {
    const user: User = JSON.parse(window.localStorage.getItem('user') as string);

    return this.axios.get(`${this.backendUrl}/team/${teamId}/trainings?${params}`, {
      headers: {
        'Measurement-System': user.measurementSystem,
      },
    });
  }

  public async updateTrainingByTeamId(teamId: string, id: string, payload: any) {
    const user: User = JSON.parse(window.localStorage.getItem('user') as string);
    return this.axios.put(`${this.backendUrl}/team/${teamId}/training/${id}`, payload, {
      headers: {
        'Measurement-System': user.measurementSystem,
      },
    });
  }

  public async getTrainingByTeamId(teamId: string, id: string) {
    const user: User = JSON.parse(window.localStorage.getItem('user') as string);
    return this.axios.get(`${this.backendUrl}/team/${teamId}/training/${id}`, {
      headers: {
        'Measurement-System': user.measurementSystem,
      },
    });
  }

  public async deleteTraining(teamId: string, id: string) {
    return this.axios.delete(`${this.backendUrl}/team/${teamId}/training/${id}`, {});
  }

  public async publishTraining(teamId: string, id: string) {
    const user: User = JSON.parse(window.localStorage.getItem('user') as string);

    return this.axios.post(
      `${this.backendUrl}/team/${teamId}/training/${id}`,
      {},
      {
        headers: {
          'x-access-token': user.token,
        },
      }
    );
  }

  public async publishAllTrainings(teamId: string, ids: string[]) {
    const config = {
      method: 'post',
      url: `${this.backendUrl}/team/${teamId}/trainings/publish`,
      headers: {
        'Content-Type': 'application/json',
      },
      data: JSON.stringify({ trainingIds: ids }),
    };

    return this.axios(config);
  }

  public async getTransferTrainingOptions(trainingDate: string) {
    const params = new URLSearchParams({
      trainingDate,
    }).toString();
    return this.axios.get(`${this.backendUrl}/athlete/trainings/transfer?${params}`);
  }

  public async transferWorkoutData(targetTrainingId: string, currentTrainingId: string) {
    const user: User = JSON.parse(window.localStorage.getItem('user') as string);

    return this.axios.post(
      `${this.backendUrl}/athlete/trainings/transfer`,
      {
        targetTraining: targetTrainingId,
        currentTraining: currentTrainingId,
      },
      {
        headers: {
          'x-access-token': user.token,
        },
      }
    );
  }

  public async logWorkoutData(trainingId: string, payload: any) {
    return this.axios.post(`${this.backendUrl}/athlete/training/${trainingId}/log`, payload, {});
  }

  public async updateActualStartTime(trainingId: string, time: any) {
    const user: User = JSON.parse(window.localStorage.getItem('user') as string);

    return this.axios.post(
      `${this.backendUrl}/v2/training/${trainingId}/update-start`,
      { actualStartTime: time },
      {
        headers: {
          'x-access-token': user.token,
          'Measurement-System': user.measurementSystem,
        },
      }
    );
  }

  public async getAthleteWorkoutDataForCoach(trainingId, memberId) {
    return this.axios.get(`${this.backendUrl}/v2/athlete/${memberId}/workout-data/${trainingId}`, {});
  }

  /////////////////////////////////////////////////////////////////
  /***********        Form Responses         *********************/
  /////////////////////////////////////////////////////////////////

  public async submitFormResponse(trainingId: number, responses: Record<string, any>) {
    return this.axios.post(`${this.backendUrl}/trainings/${trainingId}/form-responses`, {
      responses,
    });
  }

  public async getFormResponse(trainingId: number) {
    return this.axios.get(`${this.backendUrl}/trainings/${trainingId}/form-responses`);
  }

  /////////////////////////////////////////////////////////////////
  /***********        Personal Trainings         *****************/
  /////////////////////////////////////////////////////////////////

  public async getPersonalTraining(id: string) {
    const user: User = JSON.parse(window.localStorage.getItem('user') as string);

    return this.axios.get(`${this.backendUrl}/training/${id}`, {
      headers: {
        'Measurement-System': user.measurementSystem,
      },
    });
  }

  public async completePersonalTraining(trainingId: string) {
    const user: User = JSON.parse(window.localStorage.getItem('user') as string);

    return this.axios.post(
      `${this.backendUrl}/training/${trainingId}/complete`,
      {},
      {
        headers: {
          'x-access-token': user.token,
        },
      }
    );
  }

  public async incompletePersonalTraining(trainingId: string) {
    return this.axios.delete(`${this.backendUrl}/training/${trainingId}/complete`, {});
  }

  public async deletePersonalTraining(id: string) {
    return this.axios.delete(`${this.backendUrl}/training/${id}`, {});
  }

  public async updatePersonalTraining(id: string, payload: any) {
    const user: User = JSON.parse(window.localStorage.getItem('user') as string);

    return this.axios.put(`${this.backendUrl}/training/${id}`, payload, {
      headers: {
        'Measurement-System': user.measurementSystem,
      },
    });
  }

  public async createPersonalTraining(training: Training) {
    // TODO: create service for if getItem is actually null
    const user: User = JSON.parse(window.localStorage.getItem('user') as string);

    return this.axios.post(`${this.backendUrl}/trainings`, training, {
      headers: {
        'Measurement-System': user.measurementSystem,
      },
    });
  }

  public async updateAttendedStatusBulk(
    trainingId: string,
    assignees: { member_id: number; attended: boolean | null }[]
  ) {
    return this.axios.post(`${this.backendUrl}/training/${trainingId}/attended-status/bulk`, { assignees });
  }

  public resyncGarminDay(date: string, timezone: string) {
    return this.axios.post(`${this.backendUrl}/garmin/resync`, { date, timezone });
  }
}
